import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import clsx from 'clsx'
import {
  FaChevronRight,
  FaAngleDoubleDown,
  FaAngleDoubleUp,
} from 'react-icons/fa'

import {
  useGetUserWithTokenQuery,
  useGetUserCountQuery,
} from '../../services/apiSlice'
import SignIn from './SignIn'
import CreateAccount from './CreateAccount'
import Spinner from '../../components/loading/Spinner'
import { errorHandler } from '../../services/helpers'
import About from './About'

const Auth: React.FC = () => {
  const navigate = useNavigate()
  const [rightWindowState, setRightWindowState] = useState(0)
  const [leftSideState, setLeftSideState] = useState(0)
  const [rateLimitError, setRateLimitError] = useState(false)

  const {
    isSuccess,
    isLoading,
    isError: userTokenIsError,
    error: userTokenError,
  } = useGetUserWithTokenQuery(undefined, {
    refetchOnMountOrArgChange: false,
  })

  const {
    data: userCount,
    isError: userCountIsError,
    error: userCountError,
  } = useGetUserCountQuery(undefined)

  useEffect(() => {
    if (isSuccess) {
      navigate('/home', { replace: true })
    }
    if (userCountIsError) {
      const customError = errorHandler(userCountError)
      if (customError && customError.errorCode === 429) {
        setRateLimitError(true)
      }
    }
    if (userTokenIsError) {
      const customError = errorHandler(userTokenError)
      if (customError && customError.errorCode === 429) {
        setRateLimitError(true)
      }
    }
  }, [isSuccess, userCountIsError, userCountError, navigate])

  const loadingComponent = (
    <div className="flex h-dvh w-dvw items-center justify-center">
      <Spinner />
    </div>
  )

  const rateLimitDisplay = (
    <div className="flex h-dvh w-dvw items-center justify-center">
      <h1>Too many requests, please try again after a few minutes.</h1>
    </div>
  )

  if (rateLimitError) {
    return rateLimitDisplay
  }

  const mainComponent = (
    <>
      <div
        className={clsx(
          'landing-left-side z-[50] flex h-full flex-col justify-between transition-all duration-300',
          {
            'translate-y-[95%]': leftSideState === 1,
            '': leftSideState === 0,
          },
        )}
      >
        <About />
        <div className="left-side-pulldown flex h-[50px] w-full items-center justify-center">
          <button
            onClick={() => {
              setLeftSideState((curr) => {
                return curr === 0 ? 1 : 0
              })
            }}
            className="flex h-full w-full items-center justify-center text-xl"
          >
            {leftSideState === 0 ? <FaAngleDoubleDown /> : <FaAngleDoubleUp />}
          </button>
        </div>
      </div>
      <div className="landing-right-side flex h-full flex-col items-center justify-center px-[40px] py-[150px]">
        <div className="right-side-content">
          <div className="exile-regular flex h-auto w-full items-center pb-[20px] text-8xl">
            Y?
          </div>
          <div className="flex h-full w-full flex-col">
            <button
              type="button"
              onClick={() => {
                setRightWindowState(1)
              }}
              className="bgd-blue flex h-[50px] w-[250px] cursor-pointer items-center justify-center rounded-full text-base font-semibold"
            >
              Create account
            </button>
            <h1 className="text-sub mt-[20px] w-full pb-[10px] text-base font-semibold">
              Already have an account?
            </h1>
            <button
              type="button"
              onClick={() => {
                setRightWindowState(2)
              }}
              className="border-theme text-blue hover-main flex h-[50px] w-[250px] items-center justify-center rounded-full border text-base font-semibold"
            >
              Sign in
            </button>
          </div>
        </div>
        <div
          className={clsx(
            'landing-right-window border-theme bgd-theme absolute left-full z-[60] flex h-full w-full border-l transition-all duration-300',
            {
              'translate-x-[-100%]': rightWindowState !== 0,
            },
          )}
        >
          <div className="flex h-auto p-[20px]">
            <button
              type="button"
              onClick={() => setRightWindowState(0)}
              className="hover-sub text-main flex h-[50px] w-[20px] items-center justify-center rounded-full text-3xl transition-all duration-300 xl:w-[50px]"
            >
              <FaChevronRight />
            </button>
          </div>

          {rightWindowState === 1 ? (
            <CreateAccount
              userCount={userCount?.userCount ? userCount.userCount : 'unknown'}
            />
          ) : rightWindowState === 2 ? (
            <SignIn />
          ) : (
            <Spinner />
          )}
          <div className="p-[20px]">
            <div className="h-[50px] w-[20px] xl:w-[50px]"></div>
          </div>
        </div>
      </div>
    </>
  )

  return (
    <div className="landing-main h-dvh w-full">
      {isLoading && loadingComponent}
      {isSuccess && loadingComponent}
      {userTokenIsError && mainComponent}
    </div>
  )
}

export default Auth
